import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const CODES = ("AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ "
  + "DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP "
  + "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ "
  + "OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ "
  + "UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW").split(" ");

const flag = (c) => String.fromCodePoint(...[...c].map((x) => 127397 + x.charCodeAt(0)));

export function CountrySelect({ value, onChange, placeholder, testid = "country-select", className }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const countries = useMemo(() => {
    const dn = new Intl.DisplayNames([locale], { type: "region" });
    return CODES.map((code) => ({ code, name: dn.of(code) || code })).sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);
  const current = countries.find((c) => c.code === (value || "").toUpperCase());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} data-testid={testid}
          className={cn("flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm shadow-sm transition-colors hover:bg-muted/50", className)}>
          <span className={current ? "" : "text-muted-foreground"}>{current ? `${flag(current.code)}  ${current.name}` : placeholder || t("Select a country")}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command filter={(v, s) => (v.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").includes(s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "")) ? 1 : 0)}>
          <CommandInput placeholder={t("Search a country…")} data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>{t("No country found.")}</CommandEmpty>
            {countries.map((c) => (
              <CommandItem key={c.code} value={`${c.name} ${c.code}`} onSelect={() => { onChange(c.code); setOpen(false); }} data-testid={`${testid}-option-${c.code}`}>
                <span className="mr-2">{flag(c.code)}</span>{c.name}
                <Check className={cn("ml-auto w-4 h-4", current?.code === c.code ? "opacity-100" : "opacity-0")} />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
